// src/prototype/components/sep/SEPSectionImplementationRoadmap.tsx
// Section 9: Implementation Roadmap (launch posture / portfolio bucket view)
// Priority: EA Initiative Shortlist assignments → API portfolio_overview → effort fallback
// Never dump unassigned selections into Defer (DVA-UI-059).

import React, { useMemo } from 'react';
import { Zap, Layers, Eye, PauseCircle } from 'lucide-react';
import type { StrategicExecutionPlanSections } from '../../services/strategicExecutionPlanService';
import {
  mapPortfolioBucketInitiative,
  safeNumber,
  type MappedPortfolioOverviewInitiative,
} from '../../utils/mapStrategicExecutionPlan';
import { useEA, type InitiativeShortlistBucket } from '../../contexts/EAContext';

interface Finalist {
  id: string;
  title: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
  timeToValue: string;
  category: string;
  roiEstimate: { low: number; high: number; unit: string };
}

export interface SEPSectionImplementationRoadmapProps {
  finalists: Finalist[];
  clientName: string;
  portfolioOverview?: StrategicExecutionPlanSections['portfolio_overview'];
}

const BUCKET_STYLES: Record<string, {
  icon: React.ReactNode;
  bg: string;
  border: string;
  labelColor: string;
}> = {
  launch_now: {
    icon: <Zap className="w-4 h-4 text-emerald-600" />,
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
    labelColor: 'text-emerald-700',
  },
  pilot: {
    icon: <Layers className="w-4 h-4 text-[#2563EB]" />,
    bg: 'bg-blue-50',
    border: 'border-blue-100',
    labelColor: 'text-blue-700',
  },
  monitor: {
    icon: <Eye className="w-4 h-4 text-amber-600" />,
    bg: 'bg-amber-50',
    border: 'border-amber-100',
    labelColor: 'text-amber-700',
  },
  defer: {
    icon: <PauseCircle className="w-4 h-4 text-[#717182]" />,
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    labelColor: 'text-[#717182]',
  },
};

const FALLBACK_BUCKET_META: Record<string, { label: string; subtitle: string }> = {
  launch_now: { label: 'Launch Now', subtitle: 'Ready for immediate execution' },
  pilot: { label: 'Pilot First', subtitle: 'Structured pilot before full rollout' },
  monitor: { label: 'Monitor', subtitle: 'Watch and assess before committing' },
  defer: { label: 'Defer', subtitle: 'Not yet ready for this cycle' },
};

type ApiBucketKey = 'launch_now' | 'pilot' | 'monitor' | 'defer';

/** DMD-1201: BQ/API may still send legacy "scale" for Evaluate "monitor". */
function normalizeApiBucketKey(raw: string | undefined): ApiBucketKey {
  const key = String(raw || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (key === 'launch' || key === 'launch_now') return 'launch_now';
  if (key === 'pilot') return 'pilot';
  if (key === 'scale' || key === 'monitor') return 'monitor';
  if (key === 'defer') return 'defer';
  return 'monitor';
}

const SHORTLIST_TO_API: Record<InitiativeShortlistBucket, ApiBucketKey> = {
  launch: 'launch_now',
  pilot: 'pilot',
  monitor: 'monitor',
  defer: 'defer',
};

const API_BUCKET_ORDER: ApiBucketKey[] = ['launch_now', 'pilot', 'monitor', 'defer'];

/** Effort-based fallback — Defer only when explicitly assigned, never as a dump bucket. */
const getEffortBucket = (f: Finalist): ApiBucketKey => {
  if (f.implementationEffort === 'Low') return 'launch_now';
  if (f.implementationEffort === 'High') return 'monitor';
  return 'pilot';
};

const toMappedInitiative = (f: Finalist): MappedPortfolioOverviewInitiative => ({
  id: f.id,
  title: f.title,
  category: f.category,
  annualBenefitMillions: 0,
  roiEstimate: f.roiEstimate,
  timeToValue: f.timeToValue,
});

const InitiativeChip: React.FC<{
  initiative: MappedPortfolioOverviewInitiative;
  bucketStyle: (typeof BUCKET_STYLES)[string];
}> = ({ initiative, bucketStyle }) => (
  <div className={`rounded-xl border p-3.5 ${bucketStyle.bg} ${bucketStyle.border}`}>
    <p className="text-xs font-semibold text-[#18365E] leading-snug mb-1.5">{initiative.title}</p>
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] text-[#717182]">{initiative.category}</span>
      {safeNumber(initiative.annualBenefitMillions) > 0 && (
        <span className="text-[10px] font-semibold text-[#18365E]">
          ${safeNumber(initiative.annualBenefitMillions).toFixed(1)}M benefit
        </span>
      )}
      <span className="text-[10px] font-semibold text-[#FF6E4C]">
        ${safeNumber(initiative.roiEstimate.low).toFixed(1)}–{safeNumber(initiative.roiEstimate.high).toFixed(1)}{initiative.roiEstimate.unit}
      </span>
      <span className="text-[10px] text-[#717182]">{initiative.timeToValue}</span>
    </div>
  </div>
);

export const SEPSectionImplementationRoadmap: React.FC<SEPSectionImplementationRoadmapProps> = ({
  finalists, clientName, portfolioOverview,
}) => {
  const { initiativeShortlistBuckets, initiativeShortlistCount } = useEA();

  const finalistById = useMemo(
    () => new Map(finalists.map((f) => [f.id, f])),
    [finalists],
  );

  const displayBuckets = useMemo(() => {
    // 1) Prefer explicit Initiative Shortlist assignments from Evaluate
    if (initiativeShortlistCount > 0) {
      return API_BUCKET_ORDER.map((apiKey) => {
        const shortlistKey = (Object.keys(SHORTLIST_TO_API) as InitiativeShortlistBucket[])
          .find((k) => SHORTLIST_TO_API[k] === apiKey)!;
        const items = Array.from(initiativeShortlistBuckets[shortlistKey])
          .map((id) => finalistById.get(id))
          .filter((f): f is Finalist => Boolean(f));
        return {
          key: apiKey,
          label: FALLBACK_BUCKET_META[apiKey].label,
          subtitle: FALLBACK_BUCKET_META[apiKey].subtitle,
          count: items.length,
          initiatives: items.map(toMappedInitiative),
          style: BUCKET_STYLES[apiKey],
        };
      });
    }

    // 2) API portfolio_overview buckets (backend derives missing posture from effort)
    const apiBuckets = portfolioOverview?.buckets;
    if (apiBuckets?.length) {
      const bucketLegend = portfolioOverview?.bucket_legend;
      return apiBuckets.map((bucket) => {
        const apiKey = normalizeApiBucketKey(bucket.key);
        const style = BUCKET_STYLES[apiKey] ?? BUCKET_STYLES.monitor;
        const legend = bucketLegend?.find((l) => normalizeApiBucketKey(l.key) === apiKey);
        return {
          key: apiKey,
          label: FALLBACK_BUCKET_META[apiKey].label,
          subtitle:
            bucket.subtitle
            || legend?.description
            || FALLBACK_BUCKET_META[apiKey].subtitle
            || '',
          count: bucket.count,
          initiatives: bucket.initiatives.map(mapPortfolioBucketInitiative),
          style,
        };
      });
    }

    // 3) Effort-based fallback for selected finalists
    const byEffort: Record<ApiBucketKey, Finalist[]> = {
      launch_now: [],
      pilot: [],
      monitor: [],
      defer: [],
    };
    finalists.forEach((f) => byEffort[getEffortBucket(f)].push(f));

    return API_BUCKET_ORDER.map((apiKey) => {
      const items = byEffort[apiKey];
      return {
        key: apiKey,
        label: FALLBACK_BUCKET_META[apiKey].label,
        subtitle: FALLBACK_BUCKET_META[apiKey].subtitle,
        count: items.length,
        initiatives: items.map(toMappedInitiative),
        style: BUCKET_STYLES[apiKey],
      };
    });
  }, [
    finalists,
    finalistById,
    initiativeShortlistBuckets,
    initiativeShortlistCount,
    portfolioOverview,
  ]);

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-[3px] self-stretch rounded-full bg-[#FF6E4C] shrink-0 min-h-[40px]" />
        <div>
          <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-widest mb-0.5">Section 9</p>
          <h2 className="text-lg font-bold text-[#18365E] leading-tight">Implementation Roadmap</h2>
          <p className="text-[11px] text-[#717182] mt-0.5">Launch posture classification and recommended execution path per initiative</p>
        </div>
      </div>

      <p className="text-sm text-[#18365E]/80 leading-relaxed mb-6">
        Each initiative in <strong>{clientName}</strong>'s portfolio is classified by its recommended launch posture,
        reflecting organizational readiness, data maturity, and execution complexity. This roadmap guides immediate
        action prioritization and capacity planning across the full portfolio.
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {displayBuckets.map(bucket => (
          <div key={bucket.key} className="border border-[#e5e7eb] rounded-xl overflow-hidden">
            <div className={`flex items-center gap-2.5 px-4 py-3 ${bucket.style.bg} border-b ${bucket.style.border}`}>
              {bucket.style.icon}
              <div>
                <p className={`text-sm font-bold ${bucket.style.labelColor}`}>{bucket.label}</p>
                <p className="text-[10px] text-[#717182]">{bucket.subtitle}</p>
              </div>
              <div className="ml-auto">
                <span className={`text-xs font-bold ${bucket.style.labelColor}`}>{bucket.count}</span>
              </div>
            </div>
            <div className="p-3 space-y-2 bg-white min-h-[80px]">
              {bucket.initiatives.length > 0
                ? bucket.initiatives.map(init => (
                    <InitiativeChip key={init.id} initiative={init} bucketStyle={bucket.style} />
                  ))
                : <p className="text-xs text-[#717182]/60 italic px-1 py-2">No initiatives in this bucket</p>
              }
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 bg-[#F5F7FA] border border-[#e5e7eb] rounded-xl p-4">
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-2">Roadmap Interpretation Guide</p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
          {displayBuckets.map(b => (
            <div key={b.key} className="flex items-start gap-2">
              <div className="mt-1 shrink-0">{b.style.icon}</div>
              <div>
                <p className={`text-[10px] font-semibold ${b.style.labelColor}`}>{b.label}</p>
                <p className="text-[10px] text-[#717182] leading-snug">{b.subtitle}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SEPSectionImplementationRoadmap;
